import { Component, EventEmitter, Input, OnChanges, SimpleChanges, Output } from '@angular/core';

@Component({
  selector: 'app-register-password-strength',
  templateUrl: './register-password-strength.component.html',
  styleUrls: ['./register-password-strength.component.scss'],
  standalone: false
})
export class RegisterPasswordStrengthComponent implements OnChanges {
  @Input() controlTouched = false;
  @Input() dirty = false;
  @Input() password = '';
  @Input() submitted = false;
  @Output() isPasswordInvalid = new EventEmitter<boolean>();
  minLength = 12;
  maxLength = 128;
  passNum = false;
  passUpperCase = false;
  passLowerCase = false;
  passDontHaveIdenticalChars = false;
  passwordLabel = '';
  pwdPunctuation = '';
  score = 0;

  get hasError() {
    return !(
      this.validatePasswordLength(this.password) &&
      this.passNum &&
      this.passUpperCase &&
      this.passLowerCase &&
      this.passDontHaveIdenticalChars
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.password) {
      this.checkPassStrength(changes.password.currentValue as string);
    }
  }

  checkPassStrength(pass: string): void {
    this.checkPassRequirements(pass);
    this.score = this.scorePassword(pass);

    if (this.score > 80) {
      this.pwdPunctuation = 'super-strong';
      this.passwordLabel = '80';
    } else if (this.score > 60) {
      this.pwdPunctuation = 'strong';
      this.passwordLabel = '60';
    } else if (this.score >= 30) {
      this.pwdPunctuation = 'good';
      this.passwordLabel = '30';
    } else {
      this.pwdPunctuation = 'too weak';
      this.passwordLabel = '10';
    }

    if (!this.password.length) {
      this.pwdPunctuation = '';
      this.passwordLabel = '0';
    }
  }

  checkPassRequirements(pass: string): void {
    this.passNum = /\d/.test(pass);
    this.passUpperCase = /[A-Z]/.test(pass);
    this.passLowerCase = /[a-z]/.test(pass);
    this.passDontHaveIdenticalChars = /^(?!.*(.)\1\1)/.test(pass);

    if (this.passNum && this.passUpperCase && this.passLowerCase && this.passDontHaveIdenticalChars) {
      this.isPasswordInvalid.emit(!this.validatePasswordLength(pass));
      return;
    }

    this.isPasswordInvalid.emit(true);
  }

  scorePassword(pass: string): number {
    let score = 0;
    if (!pass) {
      return score;
    }

    // award every unique letter until 5 repetitions
    const letters = {};
    for (let i = 0; i < pass.length; i++) {
      letters[pass[i]] = (Number(letters[pass[i]]) || 0) + 1;
      score += 5.0 / letters[pass[i]];
    }

    // bonus points for mixing it up
    const variations = {
      len: /(?=.{12,128}$)/.test(pass),
      digits: /(?=.*\d)/.test(pass),
      nonWords: /(?=.*[[:^alnum:]])/.test(pass)
    };

    let variationCount = 0;
    for (const check in variations) {
      if (Object.prototype.hasOwnProperty.call(variations, check)) {
        variationCount += variations[check] === true ? 2 : -1;
      }
    }
    score += (variationCount - 1) * 10;

    if (this.hasError) {
      score = 10; // id no validation - just weak
    }

    return score;
  }

  validatePasswordLength(pass: string): boolean {
    const passwordRegx = new RegExp('^(?=.{12,128}$)');
    return passwordRegx.test(pass);
  }
}
